import { useState, useEffect } from 'react';
import { Link, useLocation } from 'wouter';
import { useLanguage } from '@/contexts/LanguageContext';
import Logo3D from '@/components/Logo3D';

export default function Navbar() {
  const { t, lang, setLang } = useLanguage();
  const [location] = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    // Defer scroll listener until after first paint to not block FCP
    const tid = setTimeout(() => {
      const onScroll = () => setScrolled(window.scrollY > 40);
      window.addEventListener('scroll', onScroll, { passive: true });
      return () => window.removeEventListener('scroll', onScroll);
    }, 300);
    return () => clearTimeout(tid);
  }, []);

  const navLinks = [
    { href: '/', key: 'nav_home' },
    { href: '/services', key: 'nav_services' },
    { href: '/work', key: 'nav_work' },
    { href: '/pricing', key: 'nav_pricing' },
    { href: '/faq', key: 'nav_faq' },
    { href: '/contact', key: 'nav_contact' },
  ];

  const langs: Array<{ code: 'en'|'de'; label: string }> = [
    { code: 'en', label: 'EN' },
    { code: 'de', label: 'DE' },
  ];

  return (
    <>
      <nav
        style={{
          position: 'fixed',
          top: 0, left: 0, right: 0,
          zIndex: 997,
          padding: scrolled ? '12px 0' : '20px 0',
          background: scrolled ? 'oklch(0.07 0.015 260 / 92%)' : 'transparent',
          backdropFilter: scrolled ? 'blur(20px)' : 'none',
          borderBottom: scrolled ? '1px solid oklch(1 0 0 / 6%)' : 'none',
          transition: 'all 0.3s ease',
        }}
      >
        <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Logo */}
          <Link href="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Logo3D size={38} variant="navbar" />
            <span className="navbar-brand-text" style={{
              fontFamily: 'Syne, sans-serif', fontWeight: 800,
              fontSize: '1rem', color: 'white', letterSpacing: '-0.01em',
            }}>Northgate Web</span>
          </Link>

          {/* Desktop Nav */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} className="desktop-nav">
            {navLinks.map(link => (
              <Link
                key={link.href}
                href={link.href}
                style={{
                  padding: '8px 14px',
                  borderRadius: 8,
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  color: location === link.href ? 'white' : 'oklch(0.65 0.01 260)',
                  textDecoration: 'none',
                  background: location === link.href ? 'oklch(1 0 0 / 6%)' : 'transparent',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={e => { (e.target as HTMLElement).style.color = 'white'; }}
                onMouseLeave={e => { (e.target as HTMLElement).style.color = location === link.href ? 'white' : 'oklch(0.65 0.01 260)'; }}
              >
                {t(link.key)}
              </Link>
            ))}
          </div>

          {/* Right side */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {/* Lang switcher */}
            <div className="lang-switcher" style={{ display: 'flex', gap: 2 }}>
              {langs.map(l => (
                <button
                  key={l.code}
                  className={`lang-btn${lang === l.code ? ' active' : ''}`}
                  onClick={() => setLang(l.code)}
                >
                  {l.label}
                </button>
              ))}
            </div>

            {/* CTA */}
            <Link href="/contact" className="btn-primary" style={{ padding: '9px 18px', fontSize: '0.82rem' }}>
              <span>{t('hero_cta1')}</span>
            </Link>

            {/* Hamburger */}
            <button
              onClick={() => setMenuOpen(true)}
              style={{
                display: 'none', background: 'none', border: 'none',
                color: 'white', cursor: 'pointer', padding: 4,
              }}
              className="hamburger"
              aria-label="Open menu"
            >
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                <rect y="4" width="22" height="2" rx="1" fill="currentColor"/>
                <rect y="10" width="22" height="2" rx="1" fill="currentColor"/>
                <rect y="16" width="22" height="2" rx="1" fill="currentColor"/>
              </svg>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div className={`mobile-menu${menuOpen ? ' open' : ''}`}>
        <button
          onClick={() => setMenuOpen(false)}
          style={{
            position: 'absolute', top: 24, right: 24,
            background: 'none', border: 'none', color: 'white', cursor: 'pointer',
          }}
          aria-label="Close menu"
        >
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
            <path d="M4 4l20 20M24 4L4 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
        {navLinks.map(link => (
          <Link
            key={link.href}
            href={link.href}
            onClick={() => setMenuOpen(false)}
          >
            {t(link.key)}
          </Link>
        ))}
        <Link href="/impressum" onClick={() => setMenuOpen(false)} style={{ fontSize: '1.2rem', opacity: 0.5 }}>
          {t('nav_impressum')}
        </Link>
        <div className="lang-switcher" style={{ marginTop: 16 }}>
          {langs.map(l => (
            <button
              key={l.code}
              className={`lang-btn${lang === l.code ? ' active' : ''}`}
              onClick={() => { setLang(l.code); setMenuOpen(false); }}
              style={{ fontSize: '0.9rem', padding: '8px 14px' }}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .hamburger { display: flex !important; }
        }
      `}</style>
    </>
  );
}
